import { useState, useCallback } from "react";
import { useAppContext } from "../context";

/**
 * Authentication state interface
 */
export interface AuthState {
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Authentication error interface (allauth headless format)
 */
export interface AuthError {
  message: string;
  code: string;
  param?: string;
}

/**
 * Authentication response interface
 */
export interface AuthResponse {
  success: boolean;
  user?: unknown;
  data?: { flows?: Array<{ id: string; is_pending?: boolean }> };
  errors?: AuthError[] | Record<string, string[]>; // Support both formats
  redirectUrl?: string;
  meta?: {
    session_token?: string;
    access_token?: string;
    is_authenticated?: boolean;
  };
  status?: number;
}

// Successful verification/reset may leave an anonymous session (allauth status 401).
// This completes only that action; it does not mark a login or MFA challenge complete.
function completedVerification(response: AuthResponse): AuthResponse {
  const errors = response.errors;
  const hasErrors = errors && (Array.isArray(errors) ? errors.length > 0 : Object.keys(errors).length > 0);
  return response.status === 401 && !hasErrors ? { ...response, success: true } : response;
}

/**
 * Authentication credentials interfaces
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * useAuth - Main authentication hook
 *
 * Provides authentication methods and state management integrated with AppContext.
 * Uses django-allauth headless API for all authentication operations.
 */
export const useAuth = () => {
  const { user, serverUrl } = useAppContext();
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: false,
    error: null,
    success: false,
  });

  /**
   * Set loading state
   */
  const setLoading = useCallback((loading: boolean) => {
    setAuthState((prev) => ({ ...prev, isLoading: loading }));
  }, []);

  /**
   * Clear auth state
   */
  const clearAuthState = useCallback(() => {
    setAuthState({
      isLoading: false,
      error: null,
      success: false,
    });
  }, []);

  /**
   * Get CSRF token from cookies
   */
  const getCsrfToken = useCallback((): string | null => {
    const name = "csrftoken";
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null;
    }
    return null;
  }, []);

  /**
   * Make authenticated request to allauth headless API
   */
  const makeAllauthRequest = useCallback(
    async (
      endpoint: string,
      options: RequestInit = {},
    ): Promise<AuthResponse> => {
      const csrfToken = getCsrfToken();

      try {
        const response = await fetch(`${serverUrl}/_allauth${endpoint}`, {
          ...options,
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken || "",
            ...options.headers,
          },
          credentials: "include",
        });

        let data: Record<string, unknown> = {};
        try {
          data = await response.json();
        } catch {
          // Handle non-JSON responses
          data = {
            success: false,
            errors: { non_field_errors: ["Network error"] },
          };
        }

        return {
          success: response.ok,
          ...data,
        };
      } catch {
        return {
          success: false,
          errors: { non_field_errors: ["Network error occurred"] },
        };
      }
    },
    [serverUrl, getCsrfToken],
  );

  /**
   * Parse error messages from allauth response
   */
  const parseErrors = useCallback(
    (errors?: AuthError[] | Record<string, string[]>): string | null => {
      if (!errors) return null;

      // Handle array format (allauth headless v1 format)
      if (Array.isArray(errors)) {
        return errors.map((err) => err.message).join(", ");
      }

      // Handle object format (legacy format)
      return Object.values(errors).flat().join(", ");
    },
    [],
  );

  /**
   * Handle auth response and update state
   */
  const handleAuthResponse = useCallback(
    (response: AuthResponse): AuthResponse => {
      setAuthState({
        isLoading: false,
        error: parseErrors(response.errors),
        success: response.success,
      });

      return response;
    },
    [parseErrors],
  );

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<AuthResponse> => {
      clearAuthState();
      setLoading(true);

      try {
        const response = await makeAllauthRequest("/browser/v1/auth/login", {
          method: "POST",
          body: JSON.stringify(credentials),
        });

        const result = handleAuthResponse(response);

        return result;
      } catch {
        const errorResponse = {
          success: false,
          errors: { non_field_errors: ["Network error occurred"] },
        };
        return handleAuthResponse(errorResponse);
      }
    },
    [makeAllauthRequest, handleAuthResponse, setLoading, clearAuthState],
  );

  /**
   * Request a password reset
   */
  const requestPasswordReset = useCallback(
    async (email: string): Promise<AuthResponse> => {
      clearAuthState();
      setLoading(true);

      try {
        const response = await makeAllauthRequest(
          "/browser/v1/auth/password/request",
          {
            method: "POST",
            body: JSON.stringify({ email }),
          },
        );
        return handleAuthResponse(response);
      } catch {
        const errorResponse = {
          success: false,
          errors: { non_field_errors: ["Network error occurred"] },
        };
        return handleAuthResponse(errorResponse);
      }
    },
    [makeAllauthRequest, handleAuthResponse, setLoading, clearAuthState],
  );

  /**
   * Confirm password reset with key
   */
  const confirmPasswordReset = useCallback(
    async (credentials: {
      key: string;
      password1: string;
      password2: string;
    }): Promise<AuthResponse> => {
      clearAuthState();
      setLoading(true);

      try {
        const response = await makeAllauthRequest(
          "/browser/v1/auth/password/reset",
          {
            method: "POST",
            body: JSON.stringify({ key: credentials.key, password: credentials.password1 }),
          },
        );
        return handleAuthResponse(completedVerification(response));
      } catch {
        const errorResponse = {
          success: false,
          errors: { non_field_errors: ["Network error occurred"] },
        };
        return handleAuthResponse(errorResponse);
      }
    },
    [makeAllauthRequest, handleAuthResponse, setLoading, clearAuthState],
  );

  /**
   * Request email verification
   */
  const requestEmailVerification = useCallback(async (): Promise<AuthResponse> => {
    clearAuthState();
    setLoading(true);
    if (!user?.email) {
      return handleAuthResponse({ success: false, errors: { non_field_errors: ['Sign in to resend email verification.'] } });
    }
    const response = await makeAllauthRequest('/browser/v1/account/email', {
      method: 'PUT', body: JSON.stringify({ email: user.email }),
    });
    return handleAuthResponse(response);
  }, [user, makeAllauthRequest, handleAuthResponse, setLoading, clearAuthState]);

  /**
   * Confirm email verification with key
   */
  const confirmEmailVerification = useCallback(
    async (credentials: { key: string }): Promise<AuthResponse> => {
      clearAuthState();
      setLoading(true);

      try {
        const response = await makeAllauthRequest(
          "/browser/v1/auth/email/verify",
          {
            method: "POST",
            body: JSON.stringify(credentials),
          },
        );
        return handleAuthResponse(completedVerification(response));
      } catch {
        const errorResponse = {
          success: false,
          errors: { non_field_errors: ["Network error occurred"] },
        };
        return handleAuthResponse(errorResponse);
      }
    },
    [makeAllauthRequest, handleAuthResponse, setLoading, clearAuthState],
  );

  /**
   * Change password
   */
  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string): Promise<AuthResponse> => {
      clearAuthState();
      setLoading(true);

      try {
        const response = await makeAllauthRequest(
          "/browser/v1/account/password/change",
          {
            method: "POST",
            body: JSON.stringify({
              current_password: oldPassword,
              new_password: newPassword,
            }),
          },
        );
        return handleAuthResponse(response);
      } catch {
        const errorResponse = {
          success: false,
          errors: { non_field_errors: ["Network error occurred"] },
        };
        return handleAuthResponse(errorResponse);
      }
    },
    [makeAllauthRequest, handleAuthResponse, setLoading, clearAuthState],
  );

  /**
   * End the current browser session
   */
  const logout = useCallback(async (): Promise<void> => {
    clearAuthState();
    setLoading(true);
    const response = await makeAllauthRequest('/browser/v1/auth/session', { method: 'DELETE' });
    const anonymous = response.status === 401 && response.meta?.is_authenticated === false && !response.errors;
    if (response.success || anonymous) {
      handleAuthResponse({ ...response, success: true });
      window.location.assign('/');
    } else {
      handleAuthResponse({ ...response, success: false, errors: response.errors || { non_field_errors: ['Could not sign out. Please retry.'] } });
    }
  }, [clearAuthState, setLoading, makeAllauthRequest, handleAuthResponse]);

  return {
    // Auth state
    user,
    isAuthenticated: !!user,
    authState,

    // Auth methods
    login,
    logout,
    requestPasswordReset,
    confirmPasswordReset,
    requestEmailVerification,
    confirmEmailVerification,
    changePassword,

    // Utility methods
    clearAuthState,
  };
};
